import { Component, OnInit, OnDestroy, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';

import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDividerModule } from '@angular/material/divider';

import { ProjectService } from '../project.service';
import { ApiService } from '../../../core/services/api.service';
import {
  ProjectDetailDto,
  ProjectDto,
  CreateProjectRequest,
  UpdateProjectRequest,
  PROJECT_STATUS_LABELS,
  PROJECT_PRIORITY_LABELS,
  PLATFORMS,
  PROJECT_CATEGORIES
} from '../project.models';

interface AccountOption { id: string; name: string; }
interface EmployeeOption { id: string; name: string; }
interface DepartmentOption { id: string; name: string; }

@Component({
  selector: 'app-project-form',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    MatCardModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatButtonModule,
    MatIconModule,
    MatCheckboxModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
    MatDividerModule
  ],
  templateUrl: './project-form.component.html',
  styleUrl: './project-form.component.scss'
})
export class ProjectFormComponent implements OnInit, OnDestroy {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly projectService = inject(ProjectService);
  private readonly api = inject(ApiService);
  private readonly destroy$ = new Subject<void>();

  readonly loading = signal(false);
  readonly saving = signal(false);
  readonly error = signal<string | null>(null);

  readonly accounts = signal<AccountOption[]>([]);
  readonly employees = signal<EmployeeOption[]>([]);
  readonly departments = signal<DepartmentOption[]>([]);
  readonly templates = signal<ProjectDto[]>([]);

  readonly statusOptions = Object.entries(PROJECT_STATUS_LABELS).map(([v, l]) => ({
    value: Number(v), label: l
  }));
  readonly priorityOptions = Object.entries(PROJECT_PRIORITY_LABELS).map(([v, l]) => ({
    value: Number(v), label: l
  }));
  readonly platforms = PLATFORMS;
  readonly categories = PROJECT_CATEGORIES;

  isEdit = false;
  projectId = '';

  // Form fields
  name = '';
  code = '';
  issueKeyPrefix = '';
  category = '';
  description = '';
  status = 0;
  priority = 1;
  accountId = '';
  departmentId = '';
  ownerId = '';
  startDate = '';
  endDate = '';
  dueDate = '';
  budget: number | null = null;
  color = '#6366f1';
  progress = 0;
  isActive = true;
  isPublishedApp = false;
  platform = '';
  storeUrl = '';
  packageName = '';
  isTemplate = false;
  selectedTemplateId = '';

  ngOnInit(): void {
    const id = this.route.snapshot.paramMap.get('id');
    if (id && id !== 'new') {
      this.isEdit = true;
      this.projectId = id;
      this.loadProject();
    }
    this.loadFormData();
    if (!this.isEdit) {
      this.loadTemplates();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private loadProject(): void {
    this.loading.set(true);
    this.projectService.getById(this.projectId).pipe(takeUntil(this.destroy$)).subscribe({
      next: (project) => {
        this.fillForm(project);
        this.loading.set(false);
      },
      error: (err) => {
        this.error.set(err?.message || 'Failed to load project.');
        this.loading.set(false);
      }
    });
  }

  private fillForm(p: ProjectDetailDto): void {
    this.name = p.name;
    this.code = p.code ?? '';
    this.issueKeyPrefix = p.issueKeyPrefix ?? p.code ?? '';
    this.category = p.category ?? '';
    this.description = p.description ?? '';
    this.status = p.status;
    this.priority = p.priority;
    this.accountId = p.accountId ?? '';
    this.departmentId = p.departmentId ?? '';
    this.ownerId = p.ownerId ?? '';
    this.startDate = p.startDate ?? '';
    this.endDate = p.endDate ?? '';
    this.dueDate = p.dueDate ?? '';
    this.budget = p.budget ?? null;
    this.color = p.color ?? '#6366f1';
    this.progress = p.progress ?? 0;
    this.isActive = p.isActive;
    this.isPublishedApp = p.isPublishedApp;
    this.platform = p.platform ?? '';
    this.storeUrl = p.storeUrl ?? '';
    this.packageName = p.packageName ?? '';
    this.isTemplate = !!p.isTemplate;
  }

  private loadTemplates(): void {
    this.projectService.list({ page: 1, pageSize: 100, templatesOnly: true })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (page) => this.templates.set(page.items ?? []),
        error: () => this.templates.set([])
      });
  }

  private loadFormData(): void {
    this.api.getPaged<AccountOption>('v1/accounts', { page: 1, pageSize: 200 })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (page) => this.accounts.set((page.items ?? []).map((a: any) => ({
          id: String(a.id ?? a.Id),
          name: a.name ?? a.Name
        }))),
        error: () => {}
      });

    this.api.getPaged<EmployeeOption>('v1/employees', { page: 1, pageSize: 200 })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (page) => this.employees.set((page.items ?? []).map((e: any) => ({
          id: String(e.id ?? e.Id),
          name: e.fullName ?? e.FullName ?? `${e.firstName ?? ''} ${e.lastName ?? ''}`.trim()
        }))),
        error: () => {}
      });

    this.api.getPaged<DepartmentOption>('v1/departments', { page: 1, pageSize: 200 })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (page) => this.departments.set((page.items ?? []).map((d: any) => ({
          id: String(d.id ?? d.Id),
          name: d.name ?? d.Name
        }))),
        error: () => {}
      });
  }

  get pageTitle(): string {
    return this.isEdit ? 'Edit Project' : 'Create Project';
  }

  goBack(): void {
    if (this.isEdit) {
      this.router.navigate(['/projects', this.projectId]);
    } else {
      this.router.navigate(['/projects']);
    }
  }

  save(): void {
    if (!this.name.trim()) {
      this.error.set('Project name is required.');
      return;
    }
    if (!this.ownerId) {
      this.error.set('Project owner is required.');
      return;
    }
    if (!this.selectedTemplateId) {
      if (!this.category.trim()) {
        this.error.set('Category is required.');
        return;
      }
      if (!this.issueKeyPrefix.trim()) {
        this.error.set('Issue key prefix is required.');
        return;
      }
    }

    this.saving.set(true);
    this.error.set(null);

    if (this.isEdit) {
      const request: UpdateProjectRequest = {
        name: this.name.trim(),
        code: this.code.trim() || undefined,
        issueKeyPrefix: this.issueKeyPrefix.trim().toUpperCase(),
        category: this.category.trim(),
        description: this.description.trim() || undefined,
        status: this.status,
        priority: this.priority,
        accountId: this.accountId || undefined,
        departmentId: this.departmentId || undefined,
        ownerId: this.ownerId || undefined,
        startDate: this.startDate || undefined,
        endDate: this.endDate || undefined,
        dueDate: this.dueDate || undefined,
        budget: this.budget ?? undefined,
        color: this.color || undefined,
        progress: this.progress,
        isActive: this.isActive,
        isPublishedApp: this.isPublishedApp,
        platform: this.isPublishedApp ? (this.platform || undefined) : undefined,
        storeUrl: this.isPublishedApp ? (this.storeUrl || undefined) : undefined,
        packageName: this.isPublishedApp ? (this.packageName || undefined) : undefined,
        isTemplate: this.isTemplate
      };

      this.projectService.update(this.projectId, request).pipe(takeUntil(this.destroy$)).subscribe({
        next: (project) => {
          this.saving.set(false);
          this.router.navigate(['/projects', project.id]);
        },
        error: (err) => {
          this.error.set(this.parseSaveError(err?.message || 'Failed to update project.'));
          this.saving.set(false);
        }
      });
    } else if (this.selectedTemplateId) {
      this.projectService.createFromTemplate(this.selectedTemplateId, {
        name: this.name.trim(),
        code: this.code.trim() || undefined,
        issueKeyPrefix: this.issueKeyPrefix.trim().toUpperCase() || undefined,
        ownerId: this.ownerId || undefined,
        accountId: this.accountId || undefined
      }).pipe(takeUntil(this.destroy$)).subscribe({
        next: (project) => {
          this.saving.set(false);
          this.router.navigate(['/projects', project.id]);
        },
        error: (err) => {
          this.error.set(this.parseSaveError(err?.message || 'Failed to create project from template.'));
          this.saving.set(false);
        }
      });
    } else {
      const request: CreateProjectRequest = {
        name: this.name.trim(),
        code: this.code.trim() || undefined,
        issueKeyPrefix: this.issueKeyPrefix.trim().toUpperCase(),
        category: this.category.trim(),
        description: this.description.trim() || undefined,
        priority: this.priority,
        accountId: this.accountId || undefined,
        departmentId: this.departmentId || undefined,
        ownerId: this.ownerId || undefined,
        startDate: this.startDate || undefined,
        endDate: this.endDate || undefined,
        dueDate: this.dueDate || undefined,
        budget: this.budget ?? undefined,
        color: this.color || undefined,
        isActive: this.isActive,
        isPublishedApp: this.isPublishedApp,
        platform: this.isPublishedApp ? (this.platform || undefined) : undefined,
        storeUrl: this.isPublishedApp ? (this.storeUrl || undefined) : undefined,
        packageName: this.isPublishedApp ? (this.packageName || undefined) : undefined,
        isTemplate: this.isTemplate
      };

      this.projectService.create(request).pipe(takeUntil(this.destroy$)).subscribe({
        next: (project) => {
          this.saving.set(false);
          this.router.navigate(['/projects', project.id]);
        },
        error: (err) => {
          this.error.set(this.parseSaveError(err?.message || 'Failed to create project.'));
          this.saving.set(false);
        }
      });
    }
  }

  private parseSaveError(message: string): string {
    if (/code.*(unique|already|exist|duplicate)|duplicate.*code/i.test(message)) {
      return 'Project code is already in use. Choose a different code or leave empty to auto-generate.';
    }
    if (/prefix.*(unique|already|exist|duplicate)|issuekeyprefix/i.test(message)) {
      return 'Issue key prefix is already in use within your organization.';
    }
    return message;
  }
}
